import {type MediaCardType} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {
  type Layout,
  type MediaConfigWithDefaultsAndMediaKeys,
} from '../Component';
import {getModeConfig} from '../modes/getModeConfig';
import {type TilePagination} from '../tiles';

/**
 * Calculates tile pagination for performance optimization.
 *
 * Paginates tiles when displaying large datasets:
 * - Gallery mode:
 *   - Plotly: Limited to 4 per page (performance-intensive)
 *   - Images: Limited to maintain full grid appearance (32 max, adjusted by column count)
 *   - Video: Same grid rule with a lower cap (20 max) to align with compare fan-out pagination
 * - Compare mode with fan-out: Limited to 20 rows per page to handle large fan-out dimensions
 * - Grid mode: No pagination
 * - Other media types: No pagination
 *
 * @param layout - Generated layout with tiles
 * @param config - Panel configuration with mode and pagination settings
 * @param mediaType - Type of media being displayed
 * @returns Pagination info or null if no pagination needed
 */
export function useTilePagination(
  layout: Layout,
  config: MediaConfigWithDefaultsAndMediaKeys | null,
  mediaType: MediaCardType | null
): TilePagination | null {
  return useMemo(() => {
    const columnCount = config?.columnCount ?? 1;
    const compareRowCount = layout.gridData?.rows.length ?? 0;
    const fanOutVariable = !!config?.compareSettings?.fanOutVariable;
    const modeConfig = getModeConfig(config?.mode);
    const maxTiles = modeConfig.getPageCap({
      mediaType,
      columnCount,
      layoutTileCount: layout.tiles.length,
      activeTileCount: 0,
      compareRowCount,
      fanOutVariable,
    });

    const totalTiles = modeConfig.getPaginationTileTotal({
      layoutTileCount: layout.tiles.length,
      compareRowCount,
      columnCount,
    });

    if (maxTiles == null || maxTiles >= totalTiles) {
      return null;
    }

    return {
      start: config?.page?.start ?? 0,
      size: maxTiles,
      total: totalTiles,
    };
  }, [
    config?.mode,
    config?.compareSettings?.fanOutVariable,
    mediaType,
    config?.columnCount,
    layout.tiles.length,
    layout.gridData?.rows.length,
    config?.page?.start,
  ]);
}
